import React, { useState } from 'react';
import { Trash2, Settings as SettingsIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useNavigate } from 'react-router-dom';
import NotificationSettings from './NotificationSettings';
import PrivacySettings from './PrivacySettings';

const AccountSettings = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (confirmText !== 'LÖSCHEN') return;
    
    setDeleting(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/delete-account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        localStorage.clear();
        onLogout();
        navigate('/');
      } else {
        const data = await response.json();
        alert(data.detail || 'Fehler beim Löschen des Accounts. Bitte versuche es später erneut.');
        setDeleting(false);
      }
    } catch (error) {
      console.error('Account deletion error:', error);
      alert('Fehler beim Löschen des Accounts. Bitte versuche es später erneut.');
      setDeleting(false);
    }
  };


  return (
    <div data-testid="account-settings">
      <div className="bg-white rounded-3xl shadow-xl p-4 md:p-8 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <SettingsIcon className="w-5 h-5 md:w-6 md:h-6 text-[#2B2142] flex-shrink-0" />
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">Account-Einstellungen</h2>
        </div>

        <Tabs defaultValue="general" className="space-y-6">
          <div className="bg-gray-100 rounded-xl p-1 mb-6 overflow-x-auto">
            <TabsList className="w-full min-w-max">
              <TabsTrigger value="general" className="flex-1 min-w-[100px]">Allgemein</TabsTrigger>
              {user?.role !== 'customer' && (
                <TabsTrigger value="notifications" className="flex-1 min-w-[140px]">Benachrichtigungen</TabsTrigger>
              )}
              <TabsTrigger value="privacy" className="flex-1 min-w-[120px]">Datenschutz</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="general" className="space-y-6">
            <div className="bg-white rounded-2xl">

              {/* Account Deletion */}
              <div className="border-2 border-red-200 rounded-2xl p-6 bg-red-50">
                <div className="flex items-start gap-4">
                  <Trash2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-red-900 mb-2">Account löschen</h3>
                    <p className="text-sm text-red-700 mb-4">
                      Bei Löschung deines Accounts werden deine personenbezogenen Daten entsprechend unserer Datenschutzerklärung gelöscht oder anonymisiert. Diese Aktion kann nicht rückgängig gemacht werden.
                    </p>

                    <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                      <DialogTrigger asChild>
                        <button
                          className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors"
                          data-testid="delete-account-btn"
                        >
                          Account endgültig löschen
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle className="text-2xl font-bold text-red-900">
                            Account wirklich löschen?
                          </DialogTitle>
                        </DialogHeader>

                        <div className="space-y-4" data-testid="delete-confirmation-dialog">
                          <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                            <p className="text-sm text-red-800 font-semibold mb-2">
                              ⚠️ Achtung: Diese Aktion kann nicht rückgängig gemacht werden!
                            </p>
                            <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                              <li>Alle deine Daten werden gelöscht</li>
                              <li>Dein Profil wird nicht mehr sichtbar sein</li>
                              <li>Alle Nachrichten gehen verloren</li>
                            </ul>
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Gib "LÖSCHEN" ein, um zu bestätigen:
                            </label>
                            <input
                              type="text"
                              value={confirmText}
                              onChange={(e) => setConfirmText(e.target.value)}
                              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-red-500 focus:outline-none"
                              placeholder="LÖSCHEN"
                              data-testid="delete-confirm-input"
                            />
                          </div>

                          <div className="flex gap-3">
                            <button
                              onClick={() => setShowDeleteDialog(false)}
                              className="flex-1 py-3 border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold rounded-xl"
                              data-testid="delete-cancel-btn"
                            >
                              Abbrechen
                            </button>
                            <button
                              onClick={handleDeleteAccount}
                              disabled={confirmText !== 'LÖSCHEN' || deleting}
                              className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                              data-testid="delete-confirm-btn"
                            >
                              {deleting ? 'Wird gelöscht...' : 'Endgültig löschen'}
                            </button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {user?.role !== 'customer' && (
            <TabsContent value="notifications">
              <NotificationSettings user={user} />
            </TabsContent>
          )}

          <TabsContent value="privacy">
            <PrivacySettings user={user} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AccountSettings;
